import { Component, signal, OnInit, inject, computed, LOCALE_ID, effect, untracked, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { finalize } from 'rxjs';
import { ProductService } from '../../../../services/product/product.service';
import {
  CustomerProductListItem,
  CustomerProductFilters,
} from '../../../../pages/user-dashboard/user-products/customer-product.model';
import { Loading } from '../../../../utils/loading/loading';
import { ProductCardComponent } from '../../products/product-list-component/product-card-component/product-card-component';
import { ErrorData } from '../../../../utils/error-data/error-data';
import { PharmacistCartService } from '../../../../services/cart/cart.pharmacist';
import { PharmacyCreditService } from '../../../../services/pharmacy-credit/pharmacy-credit';
import { Error } from '../../../public-user-component/auth/shared/error/error';
import { FormsModule } from '@angular/forms';
import { CartIcon } from '../../../../shared/ui/icons/cart-icon/cart-icon';
import { CartDto } from '../../../../pages/admin-dashboard/cart-management/admin-cart.model';
import { Store } from '@ngrx/store';
import { addItemToCartFnReducer } from '../../../../store/cart/cart.state';

@Component({
  selector: 'app-incoming-products',
  standalone: true,
  imports: [CommonModule, RouterLink, ProductCardComponent, Loading, ErrorData, Error, FormsModule, CartIcon],
  templateUrl: './incoming-products.html',
  styleUrl: './incoming-products.scss',
})
export class IncomingProducts implements OnInit {
  private productService = inject(ProductService);
  private pharmacistCartService = inject(PharmacistCartService);
  private pharmacyCreditService = inject(PharmacyCreditService);
  private locale = inject(LOCALE_ID);
  private store = inject(Store);
  private destroyRef = inject(DestroyRef);

  selectedPharmacy = computed(() => this.pharmacyCreditService.selectedPharmacy());
  private storeId = computed(() => this.selectedPharmacy()?.id ?? null);
  private loadedStoreId: number | null | undefined;
  totalPrice = signal(0);

  products = signal<CustomerProductListItem[]>([]);
  isLoading = signal(false);
  hasError = signal(false);
  addToCartPopup = signal<{
    active: boolean;
    productId: string;
    productPrice: number;
    unitPrice: number;
    isOffer: boolean;
    offerId?: string;
    maxQuantity: number;
  } | null>(null);
  isAddingToCart = signal(false);
  isErrorCart = signal('');
  isArabic = computed(() => this.locale.startsWith('ar'));
  itemQuantity = signal('1');
  errorQty = signal('');
  cartData = this.store.selectSignal((state) => state.cartData);

  constructor() {
    // The order limit badges depend on the Store-Id, and the header selects the pharmacy after the page loads
    effect(() => {
      const storeId = this.storeId();
      untracked(() => {
        if (this.loadedStoreId !== undefined && storeId !== this.loadedStoreId) this.loadIncomingProducts();
      });
    });
  }

  ngOnInit() {
    this.loadIncomingProducts();
  }

  loadIncomingProducts() {
    this.isLoading.set(true);
    this.hasError.set(false);

    const filters: CustomerProductFilters = {
      pageNum: 0,
      pageSize: 10,
      orderBy: 'LAST_MODIFICATION_DATE',
      orderDir: 'DESC',
    };

    this.loadedStoreId = this.storeId();
    this.productService
      .getAllCustomerProducts(filters, undefined, this.loadedStoreId)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          this.products.set(res.list);
          this.hasError.set(false);
        },
        error: () => {
          this.products.set([]);
          this.hasError.set(true);
        },
      });
  }

  onAddToCart() {
    this.isAddingToCart.set(true);

    const dto: CartDto = !this.addToCartPopup()?.isOffer
      ? {
          items: [
            {
              type: 'product',
              customerProductId: +this.addToCartPopup()?.productId!,
              quantity: +this.itemQuantity(),
            },
          ],
        }
      : {
          items: [
            {
              type: 'offer',
              offerId: +this.addToCartPopup()?.offerId!,
              products: [
                {
                  customerProductId: +this.addToCartPopup()?.productId!,
                  quantity: +this.itemQuantity(),
                },
              ],
            },
          ],
        };
    this.pharmacistCartService
      .addItemToCart('product', this.selectedPharmacy()?.id!, dto)
      .pipe(
        finalize(() => {
          this.isAddingToCart.set(false);
        }),
      )
      .subscribe({
        next: () => {
          this.addToCartPopup.set(null);
          this.store.dispatch(
            addItemToCartFnReducer({
              itemType: 'product',
              pharmacyId: this.selectedPharmacy()?.id!,
              dto,
            }),
          );
        },
        error: (err) => {
          this.isErrorCart.set(
            this.isArabic() ? err.messageAr || 'حدث بعض الخطأ' : err.messageEn || 'some error occured',
          );
        },
      });
  }

  onAddToWishlist(product: CustomerProductListItem) {
    console.log('Adding to wishlist:', product);
  }
  onActiveAddToCartPopup(e: {
    productId: string;
    quantity: string;
    unitPrice: number;
    isOffer: boolean;
    offerId?: string;
    remainingQuantity?: number | null;
  }) {
    console.log('e: ', e);
    this.isErrorCart.set('');
    this.itemQuantity.set('1');
    this.addToCartPopup.set({
      productId: e.productId,
      active: true,
      productPrice: e.unitPrice,
      unitPrice: e.unitPrice,
      isOffer: e.isOffer,
      offerId: e.offerId,
      maxQuantity: Math.min(100, e.remainingQuantity ?? 100),
    });
  }

  private maxQuantityError(max: number) {
    return this.isArabic() ? `يجب ألا تتجاوز القيمة ${max}` : `Value must not exceed ${max}`;
  }

  onQuantityChange(value: string) {
    this.errorQty.set('');
    const max = this.addToCartPopup()?.maxQuantity ?? 100;
    if (+value > max) this.errorQty.set(this.maxQuantityError(max));
    if (+value < 1)
      this.errorQty.set(this.isArabic() ? 'يجب أن تكون القيمة أكبر من صفر' : 'Value must be greater than 0');

    this.itemQuantity.set(value);
    this.addToCartPopup.update((prevData) => {
      if (!prevData) return null;
      return {
        ...prevData,
        productPrice: +value * prevData.unitPrice,
      };
    });
  }

  increaseQty() {
    this.errorQty.set('');
    const max = this.addToCartPopup()?.maxQuantity ?? 100;

    if (+this.itemQuantity() >= max) {
      this.errorQty.set(this.maxQuantityError(max));
    }
    if (+this.itemQuantity() < max) {
      this.itemQuantity.set((+this.itemQuantity() + 1).toString());
      this.addToCartPopup.update((prevData) => {
        if (!prevData) return null;

        return {
          ...prevData,
          productPrice: +this.itemQuantity() * prevData.unitPrice,
        };
      });
    }
  }
  decreaseQty() {
    this.errorQty.set('');
    if (+this.itemQuantity() === 1) {
      this.errorQty.set(this.isArabic() ? 'يجب أن تكون القيمة أكبر من صفر' : 'Value must be greater than 0');
    }
    if (+this.itemQuantity() > 1) {
      this.itemQuantity.set((+this.itemQuantity() - 1).toString());

      this.addToCartPopup.update((prevData) => {
        if (!prevData) return null;

        return {
          ...prevData,
          productPrice: +this.itemQuantity() * prevData.unitPrice,
        };
      });
    }
  }

  errorQuantity() {
    const qty = Number(this.itemQuantity());

    if (!Number.isInteger(qty)) {
      this.errorQty.set(this.isArabic() ? 'يرجى إدخال رقمًا صحيحًا' : 'Please enter a valid number.');

      return;
    }

    this.isErrorCart.set('');
  }
}
